import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/adminApi";
import { fmtRelative } from "../lib/format";
import {
  ArrowDown, ArrowUp, ChevronDown, Edit3, ExternalLink, Loader2, Plus,
  PlayCircle, Search, Trash2, X, AlertTriangle, CheckCircle2,
} from "lucide-react";

// Phase 4b · Slice 3B — Market Watchers admin page.
// Reuses existing admin console styling (Tailwind, pink accent, white cards).
// Phase C will unify the visual language across everything.

const STATUS_BADGE = {
  ok:            { bg: "bg-emerald-50",  text: "text-emerald-700", label: "OK" },
  pending:       { bg: "bg-blue-50",     text: "text-blue-700",    label: "Pending" },
  failed:        { bg: "bg-red-50",      text: "text-red-700",     label: "Failed" },
  blocked:       { bg: "bg-[#7C5CFF]/[0.08]",   text: "text-[#5B617A]",   label: "Blocked" },
  selector_miss: { bg: "bg-amber-50",    text: "text-amber-700",   label: "Selector miss" },
  timeout:       { bg: "bg-amber-50",    text: "text-amber-700",   label: "Timeout" },
};

function fmtInr(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

function diffChip(compPrice, ourPrice) {
  if (compPrice == null || ourPrice == null || ourPrice <= 0) {
    return <span className="text-[#5B617A]">—</span>;
  }
  const pct = ((ourPrice - compPrice) / ourPrice) * 100;
  if (Math.abs(pct) < 0.5) {
    return <span className="text-[#5B617A] text-xs font-semibold">≈ equal</span>;
  }
  if (pct > 0) {
    return (
      <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-bold" title="Competitor is cheaper — we're pricier">
        <ArrowDown className="w-3 h-3" />{pct.toFixed(1)}% lower
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-red-700 text-xs font-bold" title="We are cheaper than competitor">
      <ArrowUp className="w-3 h-3" />{Math.abs(pct).toFixed(1)}% higher
    </span>
  );
}

export default function MarketWatchersPage() {
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [tenantQuery, setTenantQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [planBlocked, setPlanBlocked] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [inlineResults, setInlineResults] = useState({});   // rowId -> test-scrape result
  const [busyId, setBusyId] = useState(null);
  const [modalMode, setModalMode] = useState(null);         // 'create' | 'edit' | null
  const [modalRow, setModalRow] = useState(null);

  const loadTenants = useCallback(async () => {
    try {
      const r = await api.get("/tenants", {
        params: { page: 1, page_size: 100, sort: "name", order: "asc" },
      });
      setTenants(r.data.items || []);
    } catch {
      setTenants([]);
    }
  }, []);
  useEffect(() => { loadTenants(); }, [loadTenants]);

  const loadCompetitors = useCallback(async () => {
    if (!tenantId) { setRows([]); return; }
    setLoading(true); setError(""); setPlanBlocked(false);
    try {
      const r = await api.get(
        `/tenants/${tenantId}/market/competitors`,
        { params: { include_inactive: false } },
      );
      setRows(r.data.items || []);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 403 && detail?.code === "PLAN_REQUIRED") {
        setPlanBlocked(true); setRows([]);
      } else {
        setError(detail?.detail || detail || e.message || "Failed to load");
      }
    } finally { setLoading(false); }
  }, [tenantId]);
  useEffect(() => { loadCompetitors(); }, [loadCompetitors]);

  const filteredTenants = useMemo(() => {
    if (!tenantQuery) return tenants;
    const q = tenantQuery.toLowerCase();
    return tenants.filter(
      (t) => (t.name || "").toLowerCase().includes(q) || (t.owner_email || "").toLowerCase().includes(q),
    );
  }, [tenants, tenantQuery]);

  const selectedTenant = useMemo(
    () => tenants.find((t) => t.tenant_id === tenantId || t.id === tenantId),
    [tenants, tenantId],
  );

  const runTestScrape = async (rowId) => {
    if (busyId) return;
    setBusyId(rowId);
    setExpandedId(rowId);
    setInlineResults((p) => ({ ...p, [rowId]: { loading: true } }));
    try {
      const r = await api.post(
        `/tenants/${tenantId}/market/competitors/${rowId}/test-scrape`,
      );
      setInlineResults((p) => ({ ...p, [rowId]: { loading: false, ...r.data } }));
      // Also update the row's last_scraped_* inline so the table refreshes.
      setRows((p) => p.map((row) =>
        row.id === rowId ? {
          ...row, last_scraped_price: r.data.price,
          last_scraped_at: r.data.scraped_at,
          last_scrape_status: r.data.status,
          last_scrape_error: r.data.error,
        } : row,
      ));
      // Auto-collapse after 15s.
      setTimeout(() => {
        setExpandedId((e) => (e === rowId ? null : e));
      }, 15000);
    } catch (e) {
      setInlineResults((p) => ({ ...p, [rowId]: {
        loading: false, status: "error",
        error: e.response?.data?.detail || e.message,
      } }));
    } finally { setBusyId(null); }
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`Delete watcher for "${row.competitor_name}"?`)) return;
    try {
      await api.delete(
        `/tenants/${tenantId}/market/competitors/${row.id}`,
      );
      setRows((p) => p.filter((r) => r.id !== row.id));
    } catch (e) {
      alert(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto glass-animate" data-testid="market-watchers-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="text-2xl font-extrabold text-[#0B0F1A] tracking-tight">Market Watchers</h1>
          <p className="text-sm text-[#5B617A] mt-1">
            Track competitor prices for products in the selected tenant. Daily scan at 8:30 AM IST + on-demand.
          </p>
        </div>
        <button
          data-testid="mw-add-btn"
          onClick={() => { setModalRow(null); setModalMode("create"); }}
          disabled={!tenantId || planBlocked}
          className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-gradient-to-r from-[#7C5CFF] to-[#5A3FD9] text-white font-semibold text-sm disabled:opacity-40 hover:opacity-90 transition-opacity"
        >
          <Plus className="w-4 h-4" />Add Competitor
        </button>
      </div>

      {/* Tenant selector */}
      <div className="bg-white/85 backdrop-blur-xl rounded-xl border border-[#7C5CFF]/[0.12] p-4 mb-5" data-testid="mw-tenant-picker">
        <label className="block text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider mb-2">
          Managing Tenant
        </label>
        <div className="flex gap-2 flex-wrap items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 text-[#5B617A] absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={tenantQuery}
              onChange={(e) => setTenantQuery(e.target.value)}
              placeholder="Search by name or owner email…"
              className="w-full h-10 pl-9 pr-3 bg-white/85 backdrop-blur-xl/60 border border-[#7C5CFF]/[0.12] rounded-xl text-sm focus:border-[#7C5CFF] focus:outline-none focus:ring-2 focus:ring-[#7C5CFF]/20"
              data-testid="mw-tenant-search"
            />
          </div>
          <div className="relative min-w-[260px]">
            <select
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              className="w-full h-10 pl-3 pr-9 bg-white/85 backdrop-blur-xl/60 border border-[#7C5CFF]/[0.12] rounded-xl text-sm appearance-none focus:border-[#7C5CFF] focus:outline-none focus:ring-2 focus:ring-[#7C5CFF]/20"
              data-testid="mw-tenant-select"
            >
              <option value="">— Select tenant —</option>
              {filteredTenants.map((t) => (
                <option key={t.tenant_id || t.id} value={t.tenant_id || t.id}>
                  {t.name} · {t.effective_plan}
                </option>
              ))}
            </select>
            <ChevronDown className="w-4 h-4 text-[#5B617A] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
          {selectedTenant && (
            <div className="text-xs text-[#5B617A]">
              plan: <span className="font-bold text-[#5B617A] uppercase">{selectedTenant.effective_plan}</span>
              {" · "}
              {selectedTenant.owner_email}
            </div>
          )}
        </div>
      </div>

      {/* States */}
      {!tenantId && (
        <div className="bg-white/85 backdrop-blur-xl/60 border border-[#7C5CFF]/[0.12] rounded-xl p-8 text-center" data-testid="mw-no-tenant">
          <p className="text-sm text-[#5B617A]">Select a tenant above to view or manage its market watchers.</p>
        </div>
      )}
      {tenantId && planBlocked && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex items-start gap-3" data-testid="mw-plan-blocked">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-bold text-amber-900">This tenant is on Free / Starter</p>
            <p className="text-xs text-amber-800 mt-1">
              Market Watcher requires Pro+ plan. Bump the tenant&apos;s plan from
              the <em>Tenants → Change plan</em> action, then reload this page.
            </p>
          </div>
        </div>
      )}
      {tenantId && !planBlocked && error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 flex items-start gap-3" data-testid="mw-error">
          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
          <div className="text-sm text-red-800">{error}</div>
          <button className="ml-auto text-red-700 text-xs font-bold uppercase" onClick={loadCompetitors}>Retry</button>
        </div>
      )}
      {tenantId && !planBlocked && !error && (
        <div className="bg-white/85 backdrop-blur-xl rounded-xl border border-[#7C5CFF]/[0.12] overflow-hidden" data-testid="mw-table">
          <table className="w-full text-sm">
            <thead className="bg-white/85 backdrop-blur-xl/60 border-b border-[#7C5CFF]/[0.12]">
              <tr className="text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">
                <th className="text-left px-4 py-2.5">Competitor</th>
                <th className="text-left px-4 py-2.5">Product</th>
                <th className="text-left px-4 py-2.5">Last Price</th>
                <th className="text-left px-4 py-2.5">Δ vs Ours</th>
                <th className="text-left px-4 py-2.5">Scraped</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-right px-4 py-2.5 pr-5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="p-12 text-center">
                  <Loader2 className="w-5 h-5 text-[#7C5CFF] animate-spin inline" />
                </td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="p-12 text-center text-[#5B617A] text-sm">
                  No competitors tracked yet. Click <strong>+ Add Competitor</strong> above.
                </td></tr>
              ) : rows.map((row) => {
                const st = STATUS_BADGE[row.last_scrape_status] || STATUS_BADGE.pending;
                const expanded = expandedId === row.id;
                const inline = inlineResults[row.id];
                return (
                  <React.Fragment key={row.id}>
                    <tr className="border-b border-[#7C5CFF]/[0.04] hover:bg-[#7C5CFF]/[0.06]/50" data-testid={`mw-row-${row.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-[#0B0F1A]">{row.competitor_name}</div>
                        <a href={row.competitor_url} target="_blank" rel="noreferrer"
                           className="text-[11px] text-[#5B617A] hover:text-[#7C5CFF] flex items-center gap-1 truncate max-w-[280px]"
                           title={row.competitor_url}>
                          <ExternalLink className="w-3 h-3 shrink-0" />
                          <span className="truncate">{row.competitor_url}</span>
                        </a>
                        {row.css_selector && (
                          <div className="text-[10px] text-[#5B617A] font-mono mt-0.5" title={row.css_selector}>
                            selector: {row.css_selector}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[#5B617A]">
                        <span className="text-xs font-mono text-[#5B617A]">{row.our_product_id.slice(0, 8)}</span>
                      </td>
                      <td className="px-4 py-3 font-mono tabular-nums">{fmtInr(row.last_scraped_price)}</td>
                      <td className="px-4 py-3">{diffChip(row.last_scraped_price, /* our price computed below */ null)}</td>
                      <td className="px-4 py-3 text-xs text-[#5B617A]">{row.last_scraped_at ? fmtRelative(row.last_scraped_at) : "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${st.bg} ${st.text}`}>
                          {st.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 pr-5 text-right">
                        <div className="inline-flex gap-1.5">
                          <button
                            data-testid={`mw-test-${row.id}`}
                            onClick={() => runTestScrape(row.id)}
                            disabled={busyId === row.id}
                            className="px-2.5 py-1 rounded-md border border-[#7C5CFF]/[0.12] hover:bg-[#7C5CFF]/[0.06] text-xs font-semibold text-[#5B617A] flex items-center gap-1"
                          >
                            {busyId === row.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3 h-3" />}
                            Test
                          </button>
                          <button
                            data-testid={`mw-edit-${row.id}`}
                            onClick={() => { setModalRow(row); setModalMode("edit"); }}
                            className="px-2.5 py-1 rounded-md border border-[#7C5CFF]/[0.12] hover:bg-[#7C5CFF]/[0.06] text-xs font-semibold text-[#5B617A]"
                          >
                            <Edit3 className="w-3 h-3 inline" /> Edit
                          </button>
                          <button
                            data-testid={`mw-delete-${row.id}`}
                            onClick={() => deleteRow(row)}
                            className="px-2.5 py-1 rounded-md border border-red-200 hover:bg-red-50 text-red-600 text-xs font-semibold"
                          >
                            <Trash2 className="w-3 h-3 inline" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="bg-white/85 backdrop-blur-xl/60/60" data-testid={`mw-row-expand-${row.id}`}>
                        <td colSpan={7} className="px-6 py-3">
                          {inline?.loading ? (
                            <div className="flex items-center gap-2 text-sm text-[#5B617A]">
                              <Loader2 className="w-4 h-4 animate-spin text-[#7C5CFF]" />
                              Running test scrape…
                            </div>
                          ) : inline?.status === "ok" ? (
                            <div className="flex items-center gap-4 text-sm">
                              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                              <div>
                                <strong>Extracted {fmtInr(inline.price)}</strong>
                                {inline.currency && ` (${inline.currency})`}
                                <span className="text-[#5B617A]"> · {inline.elapsed_ms} ms · via {inline.engine} · {inline.extraction_method}</span>
                              </div>
                              <button className="ml-auto text-xs text-[#5B617A] hover:text-[#5B617A]" onClick={() => setExpandedId(null)}>
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ) : inline ? (
                            <div className="flex items-start gap-2 text-sm">
                              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                              <div>
                                <strong>{STATUS_BADGE[inline.status]?.label || inline.status}</strong>
                                <p className="text-xs text-[#5B617A] mt-0.5">{inline.error || "no details"}</p>
                              </div>
                              <button className="ml-auto text-xs text-[#5B617A] hover:text-[#5B617A]" onClick={() => setExpandedId(null)}>
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modalMode && (
        <CompetitorModal
          mode={modalMode}
          initial={modalRow}
          tenantId={tenantId}
          onClose={() => { setModalMode(null); setModalRow(null); }}
          onSaved={() => { loadCompetitors(); setModalMode(null); setModalRow(null); }}
        />
      )}
    </div>
  );
}


// ---------- Add / Edit Modal ------------------------------------------------

function CompetitorModal({ mode, initial, tenantId, onClose, onSaved }) {
  const isEdit = mode === "edit" && initial;
  const [productQuery, setProductQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState(initial?.our_product_id || "");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [competitorName, setCompetitorName] = useState(initial?.competitor_name || "");
  const [url, setUrl] = useState(initial?.competitor_url || "");
  const [selector, setSelector] = useState(initial?.css_selector || "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  // Clear stale validation banner as soon as required fields become valid.
  useEffect(() => {
    if (error === "Pick a product" && productId) setError("");
  }, [productId, error]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/tenants/${tenantId}/products/search`, {
          params: { q: productQuery, limit: 20 },
        });
        setProducts(r.data.items || []);
      } catch { setProducts([]); }
    })();
  }, [tenantId, productQuery]);

  useEffect(() => {
    if (isEdit && initial?.our_product_id) {
      (async () => {
        try {
          const r = await api.get(`/tenants/${tenantId}/products/search`, {
            params: { q: "", limit: 50 },
          });
          const found = (r.data.items || []).find((p) => p.id === initial.our_product_id);
          if (found) setSelectedProduct(found);
        } catch (_e) { /* selectedProduct stays null; label falls back to the id */ }
      })();
    }
  }, [isEdit, initial?.our_product_id, tenantId]);

  const validUrl = /^https?:\/\/.+/.test(url.trim());

  const savePreview = async () => {
    setError("");
    if (!productId) { setError("Pick a product"); return; }
    if (!competitorName.trim()) { setError("Competitor name required"); return; }
    if (!validUrl) { setError("URL must be http(s)://…"); return; }
    setSaving(true);
    try {
      let row;
      if (isEdit) {
        const r = await api.patch(
          `/tenants/${tenantId}/market/competitors/${initial.id}`,
          { competitor_name: competitorName.trim(), competitor_url: url.trim(),
            css_selector: selector.trim() || null },
        );
        row = r.data;
      } else {
        const r = await api.post(
          `/tenants/${tenantId}/market/competitors`,
          { our_product_id: productId, competitor_name: competitorName.trim(),
            competitor_url: url.trim(), css_selector: selector.trim() || null },
        );
        row = r.data;
      }
      // Immediately fire test-scrape for the preview.
      setPreview({ loading: true });
      const r2 = await api.post(
        `/tenants/${tenantId}/market/competitors/${row.id}/test-scrape`,
      );
      setPreview({ loading: false, ...r2.data });
      // Give the operator a moment to see the preview, then close.
      setTimeout(onSaved, 1800);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "object" ? detail.detail || JSON.stringify(detail) : detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center" onClick={onClose} data-testid="mw-modal">
      <div className="glass-modal w-full max-w-lg mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#7C5CFF]/[0.04]">
          <h3 className="font-bold text-[#0B0F1A] text-base">
            {isEdit ? "Edit Competitor" : "Add Competitor"}
          </h3>
          <button onClick={onClose} data-testid="mw-modal-close" className="p-1.5 hover:bg-[#7C5CFF]/[0.04] rounded-md">
            <X className="w-4 h-4 text-[#5B617A]" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {error && <p className="text-red-600 text-sm font-medium bg-red-50 p-2 rounded-xl" data-testid="mw-modal-error">{error}</p>}
          <div>
            <label className="block text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider mb-1">
              Our Product
            </label>
            {!isEdit ? (
              <>
                <input
                  value={productQuery}
                  onChange={(e) => setProductQuery(e.target.value)}
                  placeholder="Search products by name…"
                  className="w-full h-10 px-3 mb-2 bg-white/85 backdrop-blur-xl/60 border border-[#7C5CFF]/[0.12] rounded-xl text-sm focus:border-[#7C5CFF] focus:outline-none focus:ring-2 focus:ring-[#7C5CFF]/20"
                  data-testid="mw-product-search"
                />
                <div className="max-h-32 overflow-auto border border-[#7C5CFF]/[0.12] rounded-xl">
                  {products.length === 0 ? (
                    <div className="p-3 text-xs text-[#5B617A]">No products found.</div>
                  ) : products.map((p) => (
                    <button
                      key={p.id}
                      data-testid={`mw-product-${p.id}`}
                      onClick={() => { setProductId(p.id); setSelectedProduct(p); }}
                      className={`w-full text-left px-3 py-2 text-sm border-b border-[#7C5CFF]/[0.04] last:border-0 hover:bg-[#7C5CFF]/[0.06]
                        ${productId === p.id ? "bg-[#7C5CFF]/10 text-[#7C5CFF]" : "text-[#5B617A]"}`}
                    >
                      <span className="font-semibold">{p.name}</span>{" "}
                      <span className="text-[#5B617A] text-xs">· {p.sku} · {fmtInr(p.sell_price)}</span>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-sm text-[#5B617A] px-3 py-2 bg-white/85 backdrop-blur-xl/60 rounded-xl">
                {selectedProduct?.name || initial.our_product_id}
              </div>
            )}
          </div>

          <div>
            <label className="block text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider mb-1">
              Competitor Name
            </label>
            <input
              value={competitorName}
              onChange={(e) => setCompetitorName(e.target.value)}
              placeholder="e.g. Amazon India, Local Shop, BigBasket"
              className="w-full h-10 px-3 bg-white/85 backdrop-blur-xl/60 border border-[#7C5CFF]/[0.12] rounded-xl text-sm focus:border-[#7C5CFF] focus:outline-none focus:ring-2 focus:ring-[#7C5CFF]/20"
              data-testid="mw-competitor-name"
            />
          </div>

          <div>
            <label className="block text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider mb-1">
              Competitor URL
            </label>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://…"
              className="w-full h-10 px-3 bg-white/85 backdrop-blur-xl/60 border border-[#7C5CFF]/[0.12] rounded-xl text-sm font-mono focus:border-[#7C5CFF] focus:outline-none focus:ring-2 focus:ring-[#7C5CFF]/20"
              data-testid="mw-competitor-url"
            />
            <p className="text-[10px] text-[#5B617A] mt-1">
              Playwright + BS4 handles JS-heavy sites. Amazon.in and Flipkart auto-detect the final discounted price.
            </p>
          </div>

          <div>
            <label className="block text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider mb-1">
              Custom CSS Selector (optional)
            </label>
            <input
              value={selector}
              onChange={(e) => setSelector(e.target.value)}
              placeholder="e.g. .a-offscreen  (leave blank to auto-detect)"
              className="w-full h-10 px-3 bg-white/85 backdrop-blur-xl/60 border border-[#7C5CFF]/[0.12] rounded-xl text-sm font-mono focus:border-[#7C5CFF] focus:outline-none focus:ring-2 focus:ring-[#7C5CFF]/20"
              data-testid="mw-selector"
            />
            <p className="text-[10px] text-[#5B617A] mt-1">
              Leave blank to auto-detect from meta tags / schema.org JSON-LD.
            </p>
          </div>

          {preview && (
            <div className={`p-3 rounded-xl border text-sm ${
              preview.loading ? "bg-blue-50 border-blue-200 text-blue-800"
              : preview.status === "ok" ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-amber-50 border-amber-200 text-amber-800"
            }`} data-testid="mw-modal-preview">
              {preview.loading ? (
                <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Running test scrape…</span>
              ) : preview.status === "ok" ? (
                <span>
                  <strong>Extracted {fmtInr(preview.price)}</strong>{" "}
                  <span className="opacity-70">in {preview.elapsed_ms} ms via {preview.engine}</span>
                </span>
              ) : (
                <span>
                  <strong>{STATUS_BADGE[preview.status]?.label || preview.status}</strong>
                  <span className="block text-xs opacity-80 mt-0.5">{preview.error}</span>
                </span>
              )}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button
              onClick={onClose}
              className="flex-1 h-10 rounded-xl border border-[#7C5CFF]/[0.12] bg-white/85 backdrop-blur-xl text-[#5B617A] font-semibold text-sm hover:bg-[#7C5CFF]/[0.06]"
              data-testid="mw-modal-cancel"
            >
              Cancel
            </button>
            <button
              onClick={savePreview}
              disabled={saving}
              className="flex-1 h-10 rounded-xl bg-gradient-to-r from-[#7C5CFF] to-[#5A3FD9] text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 hover:opacity-90"
              data-testid="mw-modal-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save & Preview"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
